<template>
  <section
    class="relative h-screen flex flex-col-reverse justify-center items-center md:flex-row py-0 gap-5 md:gap-0 overflow-hidden">
    <div
      class="w-full max-w-6xl px-4 sm:px-6">
      <h1
        class="font-bold text-2xl sm:text-3xl md:text-4xl xl:text-5xl mb-4 sm:mb-5 text-center leading-[1.15] tracking-tight text-ternary-dark dark:text-primary-light">
        <span
          v-for="(letter, index) in hiArray"
          :key="'hi' + index"
          class="animate-[bounceIn_1s_.5s] hover:animate-[rubberBand_1s] inline-block hover:text-accent"
          v-html="letter.toString()"></span
        >
      </h1>

      <h1
        class="font-bold text-[2.45rem] sm:text-7xl md:text-8xl xl:text-[8.5rem] mb-4 sm:mb-5 text-center leading-[1] sm:leading-[0.9] tracking-tight text-ternary-dark dark:text-primary-light uppercase">
        <span
          v-for="(letter, index) in nameArray"
          :key="'name' + index"
          class="animate-[bounceIn_1s_1s] hover:animate-[rubberBand_1s] inline-block hover:text-accent font-bold"
          v-html="letter.toString()"></span>
      </h1>

      <h2
        class="text-xl sm:text-2xl md:text-3xl xl:text-4xl mb-6 sm:mb-8 text-center leading-[1.2] text-ternary-dark dark:text-primary-light">
        <span
          v-for="(letter, index) in jobArray"
          :key="'job' + index"
          class="animate-[bounceIn_1s_1.5s] hover:animate-[rubberBand_1s] inline-block hover:text-accent"
          v-html="letter.toString()"></span>
      </h2>

      <p
        class="animate-[bounceIn_1s_2s] mt-2 w-full max-w-full mx-auto px-2 sm:px-4 text-base sm:text-lg md:text-xl xl:text-2xl text-center leading-[1.55] text-blueGray-500 dark:text-gray-300">
        Backend-focused software engineer with 3+ years of experience across
        web, app, and automation projects.
      </p>
    </div>

    <a
      href="/#about"
      aria-label="Scroll to about section"
      class="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-slate-400 dark:text-slate-500 hover:text-accent dark:hover:text-accent transition-colors duration-300">
      <span class="mb-1 text-[10px] font-semibold uppercase tracking-[0.28em]">
        Scroll
      </span>
      <div class="scroll-arrows">
        <span></span>
        <span></span>
      </div>
    </a>

    <transition name="fade">
      <div
        v-if="showPopup"
        class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div
          class="bg-white dark:bg-ternary-dark p-8 rounded-lg shadow-lg w-96 relative animate-popup">
          <button
            @click="showPopup = false"
            id="closePopup"
            class="absolute top-2 right-2 text-gray-500 dark:text-gray-300 text-2xl">
            &times;
          </button>
          <h2
            class="text-xl font-bold mb-4 text-ternary-dark dark:text-ternary-light">
            Share Via Email
          </h2>
          <p class="mb-4 text-gray-700 dark:text-gray-300">
            Enter your email to receive the CV:
          </p>
          <input
            type="email"
            v-model="email"
            placeholder="Your email"
            class="w-full p-2 mb-4 border border-gray-300 rounded dark:bg-gray-800 dark:text-gray-300" />
          <button
            @click="shareViaEmail"
            class="w-full bg-accent text-white py-2 rounded-lg hover:bg-accent-dark duration-300 relative">
            <span v-if="isLoading">Sending...</span>
            <span v-else>Send</span>
          </button>
          <p v-if="successMessage" class="mt-4 font-bold text-accent">
            {{ successMessage }}
          </p>
        </div>
      </div>
    </transition>
  </section>
</template>

<script setup>
import emailjs from "emailjs-com";
import feather from "feather-icons";
import { inject, onMounted, onUpdated, ref } from "vue";

const hiArray = ref([
  "H",
  "e",
  "l",
  "l",
  "o",
  "&nbsp;",
  "t",
  "h",
  "e",
  "r",
  "e",
  ",",
  "&nbsp;",
  "👋",
]);
const nameArray = ref([
  "I",
  "&nbsp;",
  "A",
  "M",
  "&nbsp;",
  "A",
  "r",
  "i",
  "f",
  "&nbsp;",
  "I",
  "s",
  "l",
  "a",
  "m",
]);

const jobArray = ref([
  "<",
  "S",
  "o",
  "f",
  "t",
  "w",
  "a",
  "r",
  "e",
  "&nbsp;",
  "E",
  "n",
  "g",
  "i",
  "n",
  "e",
  "e",
  "r",
  "/>",
]);

const theme = ref("");
const showPopup = ref(false);
const email = ref("");
const successMessage = ref("");
const isLoading = ref(false);

theme.value = localStorage.getItem("theme") || "light";
const themeState = inject("themeState");

onMounted(() => {
  feather.replace();
  theme.value = localStorage.getItem("theme") || "light";
});

onUpdated(() => {
  feather.replace();
});

const shareViaEmail = () => {
  if (email.value) {
    isLoading.value = true;
    const templateParams = {
      from_email: email.value,
      message: "Here is the CV you requested!",
    };

    emailjs
      .send(
        "service_pltbxss",
        "template_mc56lsh",
        templateParams,
        "eYhzkfzvSFELv43Cy"
      )
      .then(
        () => {
          successMessage.value = `CV will be sent to ${email.value}`;
          setTimeout(() => {
            showPopup.value = false;
            email.value = "";
            successMessage.value = "";
            isLoading.value = false;
          }, 5000);
        },
        () => {
          isLoading.value = false;
        }
      );
  } else {
    alert("Please enter a valid email.");
  }
};
</script>

<style scoped>
@keyframes popup {
  from {
    transform: scale(0.7);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-popup {
  animation: popup 0.3s ease-in-out;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

button#closePopup {
  background: transparent;
  border: none;
  cursor: pointer;
}
button#closePopup:hover {
  color: red;
}

.scroll-arrows {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.scroll-arrows span {
  display: block;
  width: 14px;
  height: 14px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  opacity: 0.2;
  animation: scrollFade 1.6s infinite;
}

.scroll-arrows span:nth-child(2) {
  animation-delay: 0.2s;
}

@keyframes scrollFade {
  0% {
    opacity: 0.15;
    transform: rotate(45deg) translate(-2px, -2px);
  }
  50% {
    opacity: 1;
    transform: rotate(45deg) translate(0, 0);
  }
  100% {
    opacity: 0.15;
    transform: rotate(45deg) translate(2px, 2px);
  }
}
</style>
